import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { serviceNeedsOptions } from '../data/mockData';
import MorphButton from '../components/MorphButton';
import { requireAuth } from '../components/AuthGate';

/**
 * Post-sign-up intake step. Every service that doesn't own a bespoke flow
 * (Ihsan Casket, Financial Assistance) lands here so no tile is a dead end.
 * The question set is placeholder copy pending the real intake requirements.
 */
export default function ServiceNeeds({ serviceId, title, description, onBack, onDone }) {
  const options = serviceNeedsOptions[serviceId] || serviceNeedsOptions.default;
  const [selected, setSelected] = useState([]);
  const [remarks, setRemarks] = useState('');

  const toggle = (option) => {
    setSelected((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  // Selections are made first, then we ask who you are. The gate is an overlay,
  // so the choices above survive the sign-up detour.
  const submit = async () => {
    if (selected.length === 0) {
      toast.error('Please select at least one option', { position: 'top-center' });
      return false;
    }
    return requireAuth();
  };

  return (
    <div className="min-h-screen bg-background md:bg-[#F3F6F5] page-fade-in" data-testid="service-needs-page">
      <div className="bg-white px-4 pt-4 pb-3 flex items-center gap-3 border-b border-border/60">
        <button type="button" data-testid="service-needs-back" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <span className="text-base font-bold text-[#0F172A] truncate">{title}</span>
      </div>

      <div className="md:max-w-[560px] md:mx-auto md:my-10 md:bg-white md:rounded-2xl md:shadow-lg md:border md:border-border/60">
        <div className="px-4 pt-5 pb-3 md:px-8">
          <h1 className="text-2xl font-extrabold text-[#0F172A]">Tell us what you need</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {description
              ? `${description}. Select everything that applies so we can point you to the right help.`
              : 'Select everything that applies so we can point you to the right help.'}
          </p>
        </div>

        <div className="px-4 md:px-8 flex flex-col gap-2.5">
          {options.map((option) => {
            const isChecked = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                data-testid={`need-option-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => toggle(option)}
                aria-pressed={isChecked}
                className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                  isChecked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                }`}
              >
                <span
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                    isChecked ? 'border-primary bg-primary text-white' : 'border-muted-foreground/40'
                  }`}
                >
                  {isChecked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
                <span className="text-sm font-medium text-[#0F172A]">{option}</span>
              </button>
            );
          })}
        </div>

        <div className="px-4 md:px-8 pt-5">
          <label className="text-xs font-semibold text-[#0F172A]" htmlFor="service-remarks">
            Remarks <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <textarea
            id="service-remarks"
            data-testid="service-needs-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={4}
            placeholder="Anything else you'd like us to know?"
            className="mt-1.5 w-full rounded-xl bg-muted/60 p-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div className="px-4 md:px-8 pt-5 pb-8">
          <MorphButton
            data-testid="service-needs-submit"
            idleLabel="Submit"
            successLabel="Request Sent"
                    onClick={submit}
            onComplete={() => onDone?.({ serviceId, selected, remarks })}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
          />
          <p className="mt-3 text-center text-[11px] leading-snug text-muted-foreground">
            Our team will follow up with you on the details.
          </p>
        </div>
      </div>
    </div>
  );
}
